import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { nanoid } from 'nanoid'

export type ToastType = 'success' | 'error' | 'info' | 'celebration'

export interface Toast {
  id: string
  message: string
  type: ToastType
}

interface ToastContextValue {
  toasts: Toast[]
  showToast: (message: string, type?: ToastType) => void
  removeToast: (id: string) => void
  celebrate: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const TOAST_DURATION = 4000 // 4 seconds
const CELEBRATION_DURATION = 6000 // 6 seconds for celebrations

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = nanoid()
      const toast: Toast = { id, message, type }

      setToasts((prev) => [...prev, toast])

      // Auto-remove after duration
      const duration = type === 'celebration' ? CELEBRATION_DURATION : TOAST_DURATION
      setTimeout(() => {
        removeToast(id)
      }, duration)
    },
    [removeToast]
  )

  // Special celebration toast with longer duration
  const celebrate = useCallback(
    (message: string) => {
      showToast(message, 'celebration')
    },
    [showToast]
  )

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast, celebrate }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
